<script>
  import { Tooltip, Button } from "paxray-components-svelte";

  export let categories = [];
  export let formatDuration;
  export let formatNumber;
  export let formatPercent;
  export let title = "Category Footprint";
  export let subtitle = "Top 10 categories by active-time share";
  export let showAction = true;
  export let fillEmptyRows = true;
  export let variant = "bars";

  $: categoryNameWidth = `${Math.min(
    12,
    Math.max(
      7,
      Math.max(
        ...categories.map(
          (category) => String(category.category || "N/A").length,
        ),
        0,
      ) * 0.48,
    ),
  )}rem`;
  $: categoryRows = [
    ...categories,
    ...(fillEmptyRows
      ? Array.from({ length: Math.max(0, 10 - categories.length) }, () => null)
      : []),
  ];

  function formatCategoryShare(value, fallback) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return fallback ?? "N/A";
    }
    return formatPercent(Math.round(Number(value)));
  }
</script>

<section class="portfolio-card">
  <div class="card-heading">
    <div>
      <div class="heading-title">
        <strong>{title}</strong><Tooltip
          text={`Shows the distribution by observed active-time share. ${subtitle}.`}
        />
      </div>
    </div>
    {#if showAction}
      <!-- <Button text="Open deep dive" kind="secondary" on:click={onDeepDive} /> -->
    {/if}
  </div>
  {#if variant === "metrics"}
    <div class="metric-grid">
      {#each categories as category}
        <div class="metric-item">
          <span>{category.category || "N/A"}</span>
          <strong>{category.valueLabel ?? formatNumber(category.value)}</strong>
        </div>
      {/each}
    </div>
  {:else}
    <div
      class="category-bars"
      style={`--category-name-width: ${categoryNameWidth};`}
    >
      {#each categoryRows as category, index}
        {#if category}
          <div
            class:last-row={index === categoryRows.length - 1}
            class="category-bar-row"
          >
            <strong class="category-label" title={category.category}
              >{category.category || "N/A"}</strong
            >
            <div class="category-bar-track">
              <span
                style={`width: ${category.barShare ?? (category.share ? Math.max(2, category.share) : 0)}%`}
              />
            </div>
            <span class="category-share">
              {formatCategoryShare(category.share, category.valueLabel)}
              <Tooltip
                text={category.tooltip ||
                  `${formatDuration(category.active_time_seconds)} active time · ${formatNumber(category.applications)} applications`}
              />
            </span>
          </div>
        {:else}
          <div class="category-bar-row placeholder" aria-hidden="true" />
        {/if}
      {/each}
    </div>
  {/if}
</section>

<style>
  .portfolio-card {
    display: flex;
    min-width: 0;
    overflow: hidden;
    flex-direction: column;
    border: 0;
    border-radius: 0.5rem;
    background: #fff;
    padding: 0 1rem 1rem;
  }
  .card-heading {
    display: flex;
    height: 4rem;
    min-height: 4rem;
    box-sizing: border-box;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .card-heading > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  }
  .heading-title {
    display: flex !important;
    flex-direction: row !important;
    align-items: center;
    gap: 0.45rem;
  }
  .card-heading {
    box-sizing: border-box;
    margin-right: -1rem;
    margin-bottom: 1rem;
    margin-left: -1rem;
    padding-right: 1rem;
    padding-left: 1rem;
  }
  .card-heading strong {
    font-size: 0.875rem;
  }
  .category-bars {
    display: grid;
    min-height: 0;
    flex: 1;
    grid-auto-rows: minmax(clamp(2rem, 3vh, 2.4rem), 1fr);
    gap: 0;
    margin-top: 1rem;
  }
  .category-bar-row {
    display: grid;
    min-height: 2.4rem;
    grid-template-columns: var(--category-name-width) minmax(5rem, 1fr) 4rem;
    align-items: center;
    gap: 0.6rem;
    padding: 0.42rem 0;
    border-bottom: 1px solid #e6e8eb;
  }
  .category-bar-row.last-row {
    border-bottom: 0;
  }
  .category-bar-row.placeholder {
    visibility: hidden;
    border-bottom: 0;
  }
  .category-label {
    min-width: 0;
    overflow: hidden;
    font-size: 0.78rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .category-bar-track {
    height: 0.42rem;
    overflow: hidden;
    border-radius: 99px;
    background: #eeeeee;
  }
  .category-bar-track span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--pax-orange);
  }
  .category-share {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
    padding-left: 0.1rem;
    font-size: 0.78rem;
    text-align: right;
  }
  .metric-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem;
    margin-top: 1rem;
  }
  .metric-item {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.65rem;
    border-radius: 0.3rem;
    background: #f3f4f5;
  }
  .metric-item span {
    color: #727b86;
    font-size: 0.65rem;
    font-weight: 600;
  }
  .metric-item strong {
    color: var(--pax-black);
    font-size: 1.1rem;
  }

  @media (max-width: 1100px) {
    .category-bar-row {
      grid-template-columns: var(--category-name-width) minmax(4rem, 1fr) 4rem;
    }
  }
</style>
